import React, { FC, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LoadingButton } from '@mui/lab';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';

export type DatePickerDialogProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (date: Date | null) => void;
    title: string;
    initialDate?: Date | null;
    loading?: boolean;
    confirmButtonLabel?: string;
    minDate?: Date;
    maxDate?: Date;
    shouldDisableDate?: (date: Date) => boolean;
    format?: string;
    disabled?: boolean;
    'data-test'?: string;
};

const DatePickerDialog: FC<DatePickerDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    initialDate,
    loading = false,
    confirmButtonLabel,
    minDate,
    maxDate,
    shouldDisableDate,
    format = getDatePickerFormat(),
    disabled = false,
    'data-test': dataTest,
}) => {
    const { t: translate } = useTranslation(['common']);
    const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate ?? null);

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleConfirm = () => {
        onConfirm(selectedDate);
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={onClose}
            title={title}
            actions={
                <LoadingButton
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!selectedDate}
                    type="submit"
                    loading={loading}
                >
                    {confirmButtonLabel || translate('common:SAVE')}
                </LoadingButton>
            }
        >
            <Box>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                    <DatePicker
                        format={format}
                        value={selectedDate}
                        onChange={handleDateChange}
                        minDate={minDate}
                        maxDate={maxDate}
                        shouldDisableDate={shouldDisableDate}
                        disabled={disabled || loading}
                        data-test={dataTest}
                    />
                </LocalizationProvider>
            </Box>
        </DHDialog>
    );
};

export default DatePickerDialog;
